import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, UserIcon, EnvelopeIcon, PhoneIcon, MapPinIcon, PaperAirplaneIcon, EyeIcon } from '@heroicons/react/24/solid';
import api from '../../utils/api';
import EmailPreviewModal from '../../components/clients/EmailPreviewModal';

const NewClient = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Email onboarding options
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [assignLawyerId, setAssignLawyerId] = useState('');
  const [availableLawyers, setAvailableLawyers] = useState([]);
  const [loadingLawyers, setLoadingLawyers] = useState(false);

  // Email preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch available lawyers on mount
  useEffect(() => {
    const fetchLawyers = async () => {
      setLoadingLawyers(true);
      try {
        const response = await api.get('/api/clients/available-lawyers/');
        setAvailableLawyers(response.data || []);
      } catch (err) {
        console.error('Failed to load lawyers:', err);
      } finally {
        setLoadingLawyers(false);
      }
    };
    fetchLawyers();
  }, []);

  const handlePreviewEmail = async () => {
    if (!firstName || !lastName || !email) {
      setError('Please fill in first name, last name, and email before previewing.');
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      const response = await api.post('/api/clients/preview-email/', {
        client_first_name: firstName,
        client_last_name: lastName,
        client_email: email,
        assign_lawyer_id: assignLawyerId ? parseInt(assignLawyerId) : null,
      });
      setPreviewHtml(response.data.html);
      setPreviewSubject(response.data.subject);
      setShowPreview(true);
    } catch (err) {
      setError('Failed to load email preview. Please try again.');
      console.error('Email preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e, forceEmail = false) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!firstName || !lastName || !email) {
      setError('Please fill in first name, last name, and email.');
      return;
    }
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    const shouldSendEmail = forceEmail || sendWelcomeEmail;

    setLoading(true);
    const clientData = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      address,
      notes,
      send_welcome_email: shouldSendEmail,
      assign_lawyer_id: assignLawyerId ? parseInt(assignLawyerId) : null,
    };

    try {
      const response = await api.post('/api/clients/', clientData);
      const data = response.data;

      if (shouldSendEmail && data.email_sent) {
        setSuccessMessage(`Client created successfully! Welcome email sent to ${email}.`);
      } else if (shouldSendEmail && !data.email_sent) {
        setSuccessMessage(`Client created, but email failed: ${data.email_message}`);
      }

      setShowPreview(false);
      // Brief delay so user sees the success message
      setTimeout(() => navigate('/clients'), 2000);
    } catch (err) {
      let errorMessage = 'Failed to create client. Please try again.';
      if (err.response?.data) {
        const errData = err.response.data;
        const fieldErrors = Object.entries(errData)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        if (fieldErrors) {
          errorMessage = fieldErrors;
        } else if (errData.detail) {
          errorMessage = errData.detail;
        }
      }
      setError(errorMessage);
      console.error('Client creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSendFromPreview = () => {
    handleSubmit(null, true);
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
      <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <button onClick={() => navigate(-1)} className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center mb-2">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </button>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Add New Client</h2>
      </div>

      {successMessage && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4 mb-6">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700 dark:text-green-300">{successMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              First Name <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last Name <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            This email will be used as the client's login for the portal.
          </p>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Phone
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PhoneIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Address
          </label>
          <div className="mt-1 relative">
             <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
               <MapPinIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
             </div>
            <textarea
              id="address"
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Notes
          </label>
          <div className="mt-1">
            <textarea
              id="notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              placeholder="Add any additional notes about this client..."
            />
          </div>
        </div>

        {/* ─── Email & Lawyer Assignment Section ─── */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-900/30 space-y-5">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
            <EnvelopeIcon className="h-5 w-5 mr-2 text-primary-500" />
            Client Onboarding Options
          </h3>

          {/* Send Welcome Email Toggle */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="sendWelcomeEmail"
                type="checkbox"
                checked={sendWelcomeEmail}
                onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="sendWelcomeEmail" className="font-medium text-gray-700 dark:text-gray-300">
                Send welcome email to client
              </label>
              <p className="text-gray-500 dark:text-gray-400">
                The client will receive an email with their portal login credentials and firm details. A user account will be created automatically.
              </p>
            </div>
          </div>

          {/* Assign Lawyer Dropdown */}
          <div>
            <label htmlFor="assignLawyer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assign a Lawyer (optional)
            </label>
            <select
              id="assignLawyer"
              value={assignLawyerId}
              onChange={(e) => setAssignLawyerId(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
            >
              <option value="">— No lawyer assigned —</option>
              {loadingLawyers ? (
                <option disabled>Loading lawyers...</option>
              ) : (
                availableLawyers.map((lawyer) => (
                  <option key={lawyer.id} value={lawyer.id}>
                    {lawyer.first_name} {lawyer.last_name} ({lawyer.role}) — {lawyer.email}
                  </option>
                ))
              )}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {sendWelcomeEmail
                ? "The assigned lawyer's details will be included in the welcome email."
                : "If sending email, the assigned lawyer's contact details will be shared with the client."}
            </p>
          </div>

          {/* Preview Email Button */}
          {sendWelcomeEmail && (
            <div>
              <button
                type="button"
                onClick={handlePreviewEmail}
                disabled={previewLoading || !firstName || !lastName || !email}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
              >
                {previewLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading Preview...
                  </>
                ) : (
                  <>
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Preview Email Before Sending
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>

          {sendWelcomeEmail ? (
            <button
              type="button"
              onClick={handlePreviewEmail}
              disabled={loading || previewLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {previewLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
              )}
              {previewLoading ? 'Loading...' : 'Preview & Send Email'}
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <CheckIcon className="h-5 w-5 mr-2" />
              )}
              {loading ? 'Saving...' : 'Save Client'}
            </button>
          )}
        </div>
      </form>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirmSend={handleConfirmSendFromPreview}
        emailHtml={previewHtml}
        emailSubject={previewSubject}
        loading={loading}
      />
    </div>
  );
};

export default NewClient;
